/**
 * T4 统一通知中心 API（docs/openapi.yaml#/components/paths/~1api~1v1~1notifications）。
 *
 * 后端契约（server/internal/notification/handler.go）：
 *  - GET    /api/v1/notifications?limit=20&before=<unix_ms>   游标分页
 *  - GET    /api/v1/notifications/unread-count                未读数
 *  - POST   /api/v1/notifications/:id/read                    单条已读
 *  - POST   /api/v1/notifications/read-all                    全部已读
 *
 * 本文件只负责 IO；UI 文案 / 跳转路由由 types.ts 集中翻译。
 * 401 与后端统一由 ApiError 处理；网络层失败（断网）抛 ApiError，调用方按
 * 既有 toast 流程展示。
 */

import { client, ApiError } from '../../../shared/api/client';
import { apiPaths } from '../../../shared/api/paths';
import type {
  NotificationListResp,
  NotificationType,
  NotificationView,
  ReadAllResp,
  UnreadCountResp,
} from '../../../shared/api/types';

export type { NotificationView } from '../../../shared/api/types';

const unreadRequests = new Map<string, Promise<number>>();

/** 把对象转成 query string；undefined / null / '' 全部跳过。 */
function buildQuery(params: Record<string, string | undefined>): string {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '') {
      qs.set(k, v);
    }
  });
  const q = qs.toString();
  return q ? `?${q}` : '';
}

/** GET /api/v1/notifications?limit=20&before=<unix_ms>
 *  - limit: 1..100；非法值直接抛 ApiError（与后端 422 语义对齐）
 *  - before: 上次返回的 next_before；首次省略 → 后端用 now+1 作为默认上界 */
export async function listNotifications(
  params: { limit?: number; before?: number } = {},
): Promise<NotificationListResp> {
  if (params.limit !== undefined && (params.limit < 1 || params.limit > 100)) {
    throw new ApiError('VALIDATION', 422, 'limit must be 1..100');
  }
  const search: Record<string, string | undefined> = {};
  if (params.limit !== undefined) {
    search.limit = String(params.limit);
  }
  if (params.before !== undefined) {
    search.before = String(params.before);
  }
  const raw = await client.get<{ items: NotificationView[]; next_before?: number }>(
    apiPaths.notifications.base + buildQuery(search),
  );
  return { data: raw.items ?? [], next_before: raw.next_before };
}

/** GET /api/v1/notifications/unread-count */
export async function getUnreadCount(type?: NotificationType): Promise<number> {
  const key = type ?? 'all';
  const existing = unreadRequests.get(key);
  if (existing) return existing;
  const suffix = type ? `?type=${encodeURIComponent(type)}` : '';
  const request = client.get<UnreadCountResp>(apiPaths.notifications.unreadCount + suffix)
    .then((resp) => resp.unread)
    .finally(() => unreadRequests.delete(key));
  unreadRequests.set(key, request);
  return request;
}

/** POST /api/v1/notifications/:id/read — 返回更新后的行 */
export async function markRead(id: string): Promise<NotificationView> {
  const resp = await client.post<NotificationView>(
    apiPaths.notifications.markRead(id),
  );
  return resp;
}

/** POST /api/v1/notifications/read-all — 返回本次标记的行数 */
export async function markAllRead(): Promise<number> {
  const resp = await client.post<ReadAllResp>(apiPaths.notifications.readAll);
  return resp.marked;
}
